import * as React from 'react'
import { Box, Form, Loader, Typography } from 'cs-kevlar-react'
import { Formik } from 'formik'
import { EditModalActionBar, FormikTextField, FormikSelect } from 'components'
import { Grid } from 'cs-kevlar-react'
import { Spacing } from 'cs-kevlar-react/lib/es/theming/baseConstants'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import {
  useCustomerApi,
  usePageNotifications,
  useLogger,
  useAuth,
  useSystemApi,
} from 'hooks'
import { hasOneOfPermissions, getAllShardMigrationStatuses } from 'utils'
import {
  SupportUserPermission,
  CustomerDatabaseConfigurationViewModel,
  DatabaseConfigurationViewModel,
  ShardMigrationStatus,
  CustomerDatabaseConfigurationRequestViewModel,
} from '../../api'
import * as Yup from 'yup'
import { Option } from 'cs-kevlar-react/lib/es/Select'
import clsx from 'clsx'
import { AsyncPayload } from '../base'
import { scheduleInterval } from 'utils/scheduling'
import { DatabaseConfigurationStatus } from './DatabaseConfigurationStatus'
import { CustomerDetailPaneBaseProps } from './customerDetailPane'

const STATUS_REFRESH_RATE_MS = 5000

interface DatabaseConfigurationProps extends CustomerDetailPaneBaseProps {}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    formFields: {
      padding: theme.spacing(8),
    },
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'auto',
    },
    gridItem: {
      width: '100%',
    },
    shardStatus: {
      marginTop: theme.spacing(8),
      marginBottom: theme.spacing(8),
    },
  }),
)

export const DatabaseConfigurationFormSchema = Yup.object().shape({
  databaseShardId: Yup.number().required('The shard field is required.'),
})

const shardMigrationStatusOptions = getAllShardMigrationStatuses()

export const DatabaseConfiguration: React.FunctionComponent<DatabaseConfigurationProps> = ({
  customerAgencyId,
  onClose,
}) => {
  const classes = useStyles({})
  const logger = useLogger()

  const { dispatch: dispatchNotification } = usePageNotifications()

  const { user: authUser } = useAuth()

  const {
    getCustomerDatabaseConfiguration,
    updateCustomerDatabaseConfiguration,
  } = useCustomerApi()
  const { getDatabaseConfiguration } = useSystemApi()

  const [
    currentDatabaseConfiguration,
    setCurrentDatabaseConfiguration,
  ] = React.useState<AsyncPayload<CustomerDatabaseConfigurationViewModel>>({
    isLoading: true,
    payload: null,
  })
  const [
    systemDatabaseConfiguration,
    setSystemDatabaseConfiguration,
  ] = React.useState<AsyncPayload<DatabaseConfigurationViewModel>>({
    isLoading: true,
    payload: null,
  })
  const [shardOptions, setShardOptions] = React.useState<
    AsyncPayload<Option[]>
  >({
    isLoading: true,
    payload: null,
  })

  React.useEffect(() => {
    const loadCurrentShardData = async () => {
      const response = await getCustomerDatabaseConfiguration(customerAgencyId)
      if (response.status === 200 && response.data) {
        setCurrentDatabaseConfiguration({
          payload: response.data,
          isLoading: false,
        })
      }
    }

    const loadSystemShardData = async () => {
      const response = await getDatabaseConfiguration()
      if (response.status === 200 && response.data) {
        setSystemDatabaseConfiguration({
          payload: response.data,
          isLoading: false,
        })

        setShardOptions({
          payload: response.data.shards.map(
            (shard) =>
              ({
                label: `${shard}`,
                key: `${shard}`,
                value: shard,
              } as Option),
          ),
          isLoading: false,
        })
      }
    }

    loadCurrentShardData()
    loadSystemShardData()
  }, [])

  const onSubmit = React.useCallback(
    async (
      values: CustomerDatabaseConfigurationViewModel,
      formikActions: any,
    ) => {
      if (
        values.databaseShardId ===
        currentDatabaseConfiguration.payload?.databaseShardId
      )
        return

      const request: CustomerDatabaseConfigurationRequestViewModel = {
        customerAgencyId: values.customerAgencyId,
        sourceDatabaseShardId: currentDatabaseConfiguration.payload
          ?.databaseShardId as string,
        destinationDatabaseShardId: values.databaseShardId,
      }
      try {
        setCurrentDatabaseConfiguration({
          payload: currentDatabaseConfiguration.payload,
          isLoading: true,
        })

        const response = await updateCustomerDatabaseConfiguration(
          customerAgencyId,
          request,
        )

        if (response.status === 200 && response.data.isSuccess) {
          dispatchNotification({
            type: 'SetSuccess',
            payload: 'Database configuration updated',
          })

          setCurrentDatabaseConfiguration({
            payload: response.data.data,
            isLoading: false,
          })
        } else {
          dispatchNotification({
            type: 'SetError',
            payload: 'There was an error updating the database configuration',
          })

          setCurrentDatabaseConfiguration({
            payload: currentDatabaseConfiguration.payload,
            isLoading: false,
          })
        }
      } catch (e) {
        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error updating the database configuration',
        })

        setCurrentDatabaseConfiguration({
          payload: currentDatabaseConfiguration.payload,
          isLoading: false,
        })
      }
    },
    [currentDatabaseConfiguration],
  )

  React.useEffect(() => {
    if (!currentDatabaseConfiguration.payload?.shardMigration) return

    if (
      [
        ShardMigrationStatus.MigratingRelatedData,
        ShardMigrationStatus.MigratingDocuments,
        ShardMigrationStatus.Pending,
      ].includes(currentDatabaseConfiguration.payload.shardMigration.status)
    ) {
      return scheduleInterval(STATUS_REFRESH_RATE_MS, async () => {
        const response = await getCustomerDatabaseConfiguration(
          customerAgencyId,
        )
        if (response.status === 200 && response.data) {
          setCurrentDatabaseConfiguration({
            payload: response.data,
            isLoading: false,
          })
        }
      })
    }
  }, [currentDatabaseConfiguration])

  const canView = hasOneOfPermissions(authUser, [
    SupportUserPermission.ViewDatabaseConfiguration,
    SupportUserPermission.ManageDatabaseConfiguration,
  ])

  if (!canView) return null

  const canEdit = hasOneOfPermissions(authUser, [
    SupportUserPermission.ManageDatabaseConfiguration,
  ])

  const isLoading =
    currentDatabaseConfiguration.isLoading ||
    shardOptions.isLoading ||
    systemDatabaseConfiguration.isLoading
  const isMigrating = [
    ShardMigrationStatus.MigratingDocuments,
    ShardMigrationStatus.MigratingRelatedData,
    ShardMigrationStatus.Pending,
    ShardMigrationStatus.CleaningUp,
  ].includes(
    currentDatabaseConfiguration.payload?.shardMigration
      ?.status as ShardMigrationStatus,
  )

  return (
    <React.Fragment>
      {currentDatabaseConfiguration.payload !== null &&
        shardOptions.payload !== null && (
          <Formik
            initialValues={currentDatabaseConfiguration.payload}
            validationSchema={DatabaseConfigurationFormSchema}
            onSubmit={onSubmit}
          >
            {(formikProps) => (
              <Form
                onSubmit={formikProps.handleSubmit}
                className={classes.form}
              >
                <Box className={classes.formFieldContainer}>
                  <Grid
                    container
                    direction="column"
                    alignItems="flex-start"
                    justifyContent="center"
                    className={classes.formFields}
                  >
                    <Grid item className={classes.gridItem}>
                      <Typography variant="body1">
                        Changing the configured database shard will disconnect
                        the connector and start a data migration.
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      container
                      className={clsx(classes.gridItem, classes.shardStatus)}
                    >
                      {currentDatabaseConfiguration.payload?.shardMigration &&
                        currentDatabaseConfiguration.payload.shardMigration
                          .status !== ShardMigrationStatus.None && (
                          <DatabaseConfigurationStatus
                            shardMigration={
                              currentDatabaseConfiguration.payload
                                .shardMigration
                            }
                          />
                        )}
                    </Grid>
                    <Grid item className={classes.gridItem}>
                      <FormikSelect
                        id="databaseShardId"
                        formikProps={formikProps}
                        label="Shard Id"
                        fieldName="databaseShardId"
                        options={shardOptions.payload as Option[]}
                        fullWidth
                        disabled={!canEdit || isMigrating}
                      />
                    </Grid>
                  </Grid>
                </Box>
                <Box className={classes.spacer} />
                <EditModalActionBar
                  onCancel={onClose}
                  canDelete={false}
                  formikProps={formikProps}
                  saveText="Save"
                  canSubmit={canEdit && !isMigrating}
                  isLoading={isLoading}
                  loader={
                    <Loader size="sm" text="Saving" disableVerticalGutters />
                  }
                />
              </Form>
            )}
          </Formik>
        )}
      {(shardOptions.payload === null ||
        currentDatabaseConfiguration.payload === null) && (
        <Loader size="sm" text="Loading database configuration" />
      )}
    </React.Fragment>
  );
}
